import React, { useState, useEffect, useCallback, useRef } from "react"
import { Spin, Select, Modal, message } from "antd"
import PageHeader from "../../components/Common/PageHeader"
import EmptyState from "../../components/Common/EmptyState"
import Table from "../../components/Common/Table"
import StoreDrawer from "../../components/Common/StoreDrawer"
import { useSelector } from "react-redux"
import { productColumns } from "../../utils/TableData/Products/productColumns"
import { productActions } from "../../utils/TableData/Products/productActionsTable"
import Dropdown from "../../components/Common/Dropdown"
import useProducts from "../../services/useProduct"
import { productCategories } from "../../utils/constants"
import { Form } from "antd"
import ProductFormItem from "../../components/Products/ProductFormItem"
import { jwtDecode } from "jwt-decode"
import { getUserCookies } from "../../utils/Cookies"
import { translations } from "../../utils/Translation/staticTranslation"
import "./../../styles/Pages/products.css"
import DeleteConfirmationModal from "../../components/Common/Modals/DeleteConfirmationModal"

const Products = () => {
  const [form] = Form.useForm()
  const highest_role =
    getUserCookies()?.token && jwtDecode(getUserCookies()?.token).highest_role

  const stores = useSelector((state) => state.store.stores)
  const [selectedStore, setSelectedStore] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("Name")
  const [filterStatus, setFilterStatus] = useState("")
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [productToDelete, setProductToDelete] = useState(null)

  const showDrawer = () => setDrawerVisible(true)
  const closeDrawer = () => setDrawerVisible(false)

  // Use this ref to prevent duplicate API calls on initial render
  const isInitialMount = useRef(true)

  const lang = localStorage.getItem("language") || "ar"
  const product = translations[lang]?.productPage

  const filters = translations[lang]?.filters
  const btn = translations[lang]?.common_btn
  const { common_error_msg } = translations[lang]

  const handleEditProduct = (product) => {
    setSelectedProduct(product)
    setEditModalVisible(true)
  }

  // Close Edit Modal
  const closeEditModal = () => {
    setSelectedProduct(null)
    setEditModalVisible(false)
  }

  const handleDownloadTemplate = () => {
    const link = document.createElement("a")
    link.href =
      "https://mesaky-products-backup.s3.ap-south-1.amazonaws.com/products_template1.xlsx"
    link.setAttribute("download", "products_template.xlsx")
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Use debounced search to optimize API calls
  const debouncedSetSearchTerm = useCallback((value) => {
    const handler = setTimeout(() => {
      setSearchTerm(value || "")
    }, 300)

    return () => {
      clearTimeout(handler)
    }
  }, [])

  const handleSearchChange = (e) => {
    debouncedSetSearchTerm(e.target.value)
  }

  const handleCategoryTypeChange = (value) => {
    // Reset search term when changing category type
    setSearchTerm("")
    setSelectedCategory(value)
  }

  const handleStatusFilterChange = (value) => {
    setFilterStatus(value === "All" ? "" : value)
  }

  const {
    products,
    loading,
    currentPage,
    setCurrentPage,
    handleDeleteProduct,
    handleUpdateProduct,
    updateLoading,
    deleteLoading,
  } = useProducts(
    selectedStore,
    selectedCategory,
    searchTerm,
    filterStatus,
    closeEditModal,
    selectedProduct,
    setDeleteModalVisible
  )

  const onFinish = (values) => {
    if (selectedProduct) {
      handleUpdateProduct(values, selectedProduct, "", undefined)
    }
  }

  const hasProducts = products?.results && products.results.length > 0

  const itemsPerPage = 10
  const start = (currentPage - 1) * itemsPerPage + 1
  const end = Math.min(start + products?.results?.length - 1, products?.count)

  const totalPages = products?.count
    ? Math.ceil(products.count / itemsPerPage)
    : 0

  const getPageNumbers = () => {
    const pageNumbers = []
    const maxPagesToShow = 5

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i)
      }
    } else {
      pageNumbers.push(1)

      if (currentPage < 3) {
        pageNumbers.push(2, 3, 4, "...", totalPages)
      } else if (currentPage > totalPages - 2) {
        pageNumbers.push(
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        )
      } else {
        if (currentPage > 3) pageNumbers.push("...")
        pageNumbers.push(currentPage - 1, currentPage, currentPage + 1)
        if (currentPage < totalPages - 2) pageNumbers.push("...")
        pageNumbers.push(totalPages)
      }
    }

    return pageNumbers
  }

  // Reset current page when filters change, but not on initial render
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    setCurrentPage(1)
  }, [
    selectedStore,
    selectedCategory,
    searchTerm,
    filterStatus,
    setCurrentPage,
  ])

  const handleCancelDelete = useCallback(() => {
    setDeleteModalVisible(false)
    setProductToDelete(null)
  }, [])

  const handleDeleteProductConfirmation = () => {
    if (!productToDelete.store) {
      message.error(common_error_msg?.select_store_to_delete_product)
      return
    }
    handleDeleteProduct(productToDelete)
  }
  const handleDeleteProductModalOpen = (row) => {
    setProductToDelete(row)
    setDeleteModalVisible(true)
  }

  const modal = translations[lang]?.modals

  return (
    <div>
      <DeleteConfirmationModal
        deleteLoading={deleteLoading}
        visible={deleteModalVisible}
        onCancel={handleCancelDelete}
        onConfirm={handleDeleteProductConfirmation}
        title={modal?.confirm_delete_text}
        content={
          <>
            {modal?.description_1}{" "}
            <b>
              {" "}
              {lang === "ar"
                ? productToDelete?.name_arabic
                : productToDelete?.name_english}
            </b>{" "}
            {modal?.description_2}
          </>
        }
      />

      <PageHeader
        selectedStore={selectedStore}
        title={product?.products}
        buttonText={product?.add_products}
        dropdownItems={[
          {
            label: product?.add_manually,
            path: `/products/add-manual/${selectedStore?.id}`,
          },
          {
            label: product?.bulk_upload,
            path: `/products/bulk-upload/${selectedStore?.id}`,
          },
          {
            label:
              product?.bulk_upload_page?.download_template ||
              "Download Template",
            onClick: handleDownloadTemplate,
          },
        ]}
        showDrawer={showDrawer}
        storeSelectionText={product?.select_store}
      />

      <div className="main-content-container">
        <div
          className="d-flex mb-24 align-items-center"
          style={{ height: "45px" }}
        >
          <Dropdown
            selectedCategory={selectedCategory}
            label={filters?.select}
            iconSrc="/icons/selectstoreanalytics.svg"
            options={[
              { label: product?.dropdown_1?.name, value: "Name" },
              { label: product?.dropdown_1?.category, value: "Category" },
            ]}
            onSelect={handleCategoryTypeChange}
          />

          <div
            style={{
              marginRight: lang === "ar" ? "0px" : "10px",
              marginLeft: lang === "ar" ? "10px" : "0px",
            }}
          >
            {selectedCategory === "Category" ? (
              <>
                <Select
                  style={{
                    width: "100%",
                    minWidth: "249px",
                    marginRight: lang === "ar" ? "0px" : "12px",
                    marginLeft: lang === "ar" ? "12px" : "0px",
                    alignItems: "center",
                  }}
                  className="product-search"
                  showSearch
                  placeholder={filters?.select_categories}
                  allowClear
                  onChange={(value) => setSearchTerm(value || "")}
                  options={productCategories.map((item) => ({
                    value: item.value,
                    label: lang === "ar" ? item.label_ar : item.label_en,
                  }))}
                />
              </>
            ) : (
              <>
                <input
                  type="search"
                  className="search-store"
                  placeholder={filters?.search}
                  onChange={handleSearchChange}
                />
              </>
            )}
          </div>

          <Dropdown
            selectedCategory={filterStatus || filters?.all}
            label={filters?.all}
            iconSrc="/icons/selectstoreanalytics.svg"
            options={[
              { label: product?.dropdown_2?.all, value: "All" },
              { label: product?.dropdown_2?.active, value: "Active" },
              { label: product?.dropdown_2?.inactive, value: "Inactive" },
            ]}
            onSelect={handleStatusFilterChange}
          />
        </div>
        {loading ? (
          <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <Spin size="large" />
          </div>
        ) : hasProducts ? (
          <>
            <Table
              columns={productColumns}
              data={products?.results}
              actions={
                highest_role === "OWNER" || highest_role === "ADMIN"
                  ? productActions(
                      handleDeleteProductModalOpen,
                      handleDeleteProductConfirmation,
                      handleEditProduct,
                      handleUpdateProduct,
                      updateLoading,
                      lang,
                      product
                    )
                  : null
              }
            />

            {products?.count > 0 && (
              <div className="d-flex justify-content-between mt-4 align-items-center pagination-wrapper">
                <div className="results-info">
                  <span className="font-medium text-gray-600">
                    {product?.showing}{" "}
                    <span className="font-bold text-gray-800">
                      {start} - {end}
                    </span>{" "}
                    {product?.of}{" "}
                    <span className="font-bold text-gray-800">
                      {products?.count} {product?.products}
                    </span>
                  </span>
                </div>

                <div className="pagination-container">
                  <button
                    onClick={() =>
                      setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className={`pagination-nav-btn ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <span className="nav-arrow">
                      {lang === "ar" ? "→" : "←"}{" "}
                    </span>
                    <span className="nav-text">{product?.previous}</span>
                  </button>

                  <div className="pagination-numbers">
                    {getPageNumbers().map((pageNum, index) => (
                      <React.Fragment key={index}>
                        {pageNum === "..." ? (
                          <span className="pagination-ellipsis">•••</span>
                        ) : (
                          <button
                            className={`pagination-number-btn ${
                              currentPage === pageNum ? "active-page" : ""
                            }`}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  <button
                    disabled={currentPage >= totalPages}
                    onClick={() =>
                      setCurrentPage((prevPage) =>
                        Math.min(prevPage + 1, totalPages)
                      )
                    }
                    className={`pagination-nav-btn ${
                      currentPage >= totalPages ? "disabled" : ""
                    }`}
                  >
                    <span className="nav-text">{product?.next}</span>{" "}
                    <span className="nav-arrow">
                      {" "}
                      {lang === "ar" ? "←" : "→"}{" "}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            title={product?.no_product?.title}
            description={
              selectedStore
                ? product?.no_product?.selected_store_no_product
                : product?.no_product?.no_product_in_any_store
            }
            buttonPath="/products/create-products"
          />
        )}
      </div>

      {/* Ant Design Drawer */}
      <StoreDrawer
        setCurrentPage={setCurrentPage}
        visible={drawerVisible}
        onClose={closeDrawer}
        stores={stores}
        selectedStore={selectedStore}
        onSelectStore={setSelectedStore}
        AllFilterOption={product?.stores}
        title={product?.select_store}
      />
      <Modal
        width={800}
        title={product?.update_product}
        open={editModalVisible}
        onCancel={closeEditModal}
        footer={null}
        destroyOnClose
      >
        {selectedProduct && (
          <Form onFinish={onFinish} form={form} layout="vertical">
            <ProductFormItem
              form={form}
              formItem={{ id: "1" }}
              product={selectedProduct}
            />
            <div className="text-end">
              <button
                disabled={updateLoading[selectedProduct?.id]}
                type="submit"
                className={`Add-btn bg-504943 ${
                  updateLoading[selectedProduct?.id] && "not-allowed"
                }`}
              >
                {updateLoading[selectedProduct?.id]
                  ? btn?.loading
                  : btn?.update}
              </button>
            </div>
          </Form>
        )}
      </Modal>
    </div>
  )
}

export default Products
